import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Menu } from '~/eds/src/components/Menu/Menu';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { PilotId } from '~/eds/src/types/PilotId';
import { TableViewContextType } from '~/enums/TableViewType';
import { api } from '~/redux/api/createApi';
import { TableView } from '~/redux/api/methods/tableViews';
import { MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH } from '~/types/modal.types';
import { Nullable } from '~/types/Nullable';

interface Props {
  /** if undefined, the default context is Documents table. */
  context: TableViewContextType | undefined;
  modalType: string;
  hideModal: () => void;
  onViewSelected: (id: PilotId) => void;
}

const BaseLoadView = ({
  context,
  modalType,
  hideModal,
  onViewSelected,
}: Props) => {
  const intl = useIntl();
  const [search, setSearch] = useState<Nullable<string>>('');
  const [newName, setNewName] = useState<Nullable<string>>('');
  const [selected, setSelected] = useState<Nullable<TableView>>();
  const { toast } = useToast();

  const { data: tableViews, isLoading: isLoadingTableViews } =
    api.endpoints.getTableViews.useQuery({
      context,
    });

  const [loadTableView, loadTableViewResult] =
    api.endpoints.loadTableView.useMutation();
  const {
    data: loadTableResponseData,
    isError: loadTableError,
    isSuccess: loadTableSuccess,
  } = loadTableViewResult;

  const [loadDefaultTableView, loadDefaultTableViewResult] =
    api.endpoints.loadDefaultTableView.useMutation();
  const {
    isError: loadDefaultTableViewError,
    isSuccess: loadDefaultTableViewSuccess,
    data: loadDefaultTableViewData,
  } = loadDefaultTableViewResult;

  const [renameTableView, renameResult] =
    api.endpoints.renameTableView.useMutation();
  const { isLoading: isRenaming } = renameResult;

  const [deleteTableView, deleteResult] =
    api.endpoints.deleteTableView.useMutation();
  const { isLoading: isDeleting } = deleteResult;

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [renameVisible, setRenameVisible] = useState(false);

  // adds the context to segment.
  const segmentFeature = context ? context.toLowerCase() : 'documents page';

  useEffect(() => {
    if (loadTableSuccess && loadTableResponseData) {
      onViewSelected(loadTableResponseData.id);
      trackSegment(
        `New view selected for ${segmentFeature}`,
        loadTableResponseData,
      );
      hideModal();
    }
  }, [loadTableSuccess]);

  useEffect(() => {
    if (loadDefaultTableViewSuccess) {
      onViewSelected('DEFAULT');
      trackSegment(
        `Default view selected for ${segmentFeature}`,
        loadDefaultTableViewData,
      );
      hideModal();
    }
  }, [loadDefaultTableViewSuccess]);

  useEffect(() => {
    if (loadTableError) {
      toast({
        message: intl.formatMessage({
          id: 'BaseLoadView.loadViewError',
          defaultMessage: 'Error on loading view. Please try again.',
          description: 'Toast message for error loading a saved view',
        }),
        status: 'danger',
      });
      hideModal();
    }
  }, [loadTableError]);

  useEffect(() => {
    if (loadDefaultTableViewError) {
      toast({
        message: intl.formatMessage({
          id: 'BaseLoadView.restoreDefaultViewError',
          defaultMessage: 'Error on restoring default view. Please try again.',
          description: 'Toast message for error restoring default view',
        }),
        status: 'danger',
      });
      hideModal();
    }
  }, [loadDefaultTableViewError]);

  const handleOpenEdit = (tableView: TableView) => {
    setSelected(tableView);
    setNewName(tableView.name);
    setRenameVisible(true);
  };
  const handleOpenDelete = (tableView: TableView) => {
    setSelected(tableView);
    setConfirmVisible(true);
  };

  const handleCloseRename = () => {
    setSelected(undefined);
    setNewName('');
    setRenameVisible(false);
  };
  const handleCloseDelete = () => {
    setSelected(undefined);
    setConfirmVisible(false);
  };

  const handleViewSelected = useCallback((id: number) => {
    loadTableView({ context, id });
  }, []);

  const handleDefaultViewSelected = useCallback(() => {
    loadDefaultTableView({ context });
  }, []);

  const handleRename = async () => {
    if (selected) {
      try {
        await renameTableView({
          id: selected.id,
          name: newName ?? '',
        }).unwrap();
        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'BaseLoadView.renameSuccess',
            defaultMessage: 'View has been renamed successfully',
            description: 'Toast message for successful view rename',
          }),
        });
      } catch {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'BaseLoadView.renameError',
            defaultMessage: 'Error renaming view',
            description: 'Toast message for error renaming view',
          }),
        });
      }
    }
    setRenameVisible(false);
  };

  const handleDelete = async () => {
    if (selected) {
      try {
        await deleteTableView({ id: selected.id }).unwrap();
        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'BaseLoadView.deleteSuccess',
            defaultMessage: 'View has been deleted successfully',
            description: 'Toast message for successful view deletion',
          }),
        });
      } catch {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'BaseLoadView.deleteError',
            defaultMessage: 'Error deleting view',
            description: 'Toast message for error deleting view',
          }),
        });
      }
    }
    setConfirmVisible(false);
  };

  if (modalType !== MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH) return null;

  const noResults = !isLoadingTableViews && !tableViews?.length;

  return (
    <Panel
      title={intl.formatMessage({
        id: 'BaseLoadView.title',
        defaultMessage: 'Saved Views',
        description: 'Title for the saved views modal',
      })}
      hidden={{
        onHide: hideModal,
      }}
      width="m"
      mode="floating"
      enableBackdrop
    >
      <ContentContainer loadingContent={{ isLoading: isLoadingTableViews }}>
        <Layout w="100%" direction="column" spacing={4}>
          <SearchInput
            name="search viewset"
            placeholder={intl.formatMessage({
              id: 'BaseLoadView.searchPlaceholder',
              defaultMessage: 'Filter saved views…',
              description: 'Placeholder text for saved views search input',
            })}
            value={search}
            onChange={setSearch}
          />

          <Layout direction="column">
            {'default view'.includes((search ?? '').toLowerCase()) && (
              <Layout
                align="center"
                justify="space-between"
                borderBottom="border"
                py={2}
              >
                <Text>
                  {intl.formatMessage({
                    id: 'BaseLoadView.defaultView',
                    defaultMessage: 'Default View',
                    description: 'Label for the default view',
                  })}
                </Text>
                <Layout>
                  <Box mr={2}>
                    <Button
                      icon="search"
                      text={intl.formatMessage({
                        id: 'BaseLoadView.applyDefaultView',
                        defaultMessage: 'Apply',
                        description: 'Button text for applying default view',
                      })}
                      onClick={handleDefaultViewSelected}
                    />
                  </Box>

                  <Menu
                    disabled
                    trigger={
                      <Icon
                        icon="more"
                        tooltip={intl.formatMessage({
                          id: 'BaseLoadView.moreOptions',
                          defaultMessage: 'More Options',
                          description: 'Tooltip for more options menu',
                        })}
                      />
                    }
                    name="More Options"
                    options={[]}
                  />
                </Layout>
              </Layout>
            )}

            {tableViews
              ?.filter((view) =>
                view.name.toLowerCase().includes((search ?? '').toLowerCase()),
              )
              ?.map((view) => (
                <Layout
                  key={`view-${view.id}`}
                  align="center"
                  justify="space-between"
                  borderBottom="border"
                  py={2}
                >
                  <Text>{view.name}</Text>
                  <Layout>
                    <Box mr={2}>
                      <Button
                        icon="search"
                        text={intl.formatMessage({
                          id: 'BaseLoadView.applyView',
                          defaultMessage: 'Apply',
                          description: 'Button text for applying a saved view',
                        })}
                        onClick={() => handleViewSelected(view.id)}
                      />
                    </Box>

                    <Menu
                      trigger={
                        <Icon
                          icon="more"
                          tooltip={intl.formatMessage({
                            id: 'BaseLoadView.moreOptions',
                            defaultMessage: 'More Options',
                            description: 'Tooltip for more options menu',
                          })}
                        />
                      }
                      name={intl.formatMessage({
                        id: 'BaseLoadView.moreOptionsMenu',
                        defaultMessage: 'More Options',
                        description: 'Name for more options menu',
                      })}
                      options={[
                        {
                          label: intl.formatMessage({
                            id: 'BaseLoadView.renameOption',
                            defaultMessage: 'Rename',
                            description: 'Menu option for renaming a view',
                          }),
                          value: 'rename',
                          onClick: () => handleOpenEdit(view),
                        },
                        {
                          label: intl.formatMessage({
                            id: 'BaseLoadView.deleteOption',
                            defaultMessage: 'Delete',
                            description: 'Menu option for deleting a view',
                          }),
                          value: 'delete',
                          onClick: () => handleOpenDelete(view),
                        },
                      ]}
                    />
                  </Layout>
                </Layout>
              ))}

            {noResults && (
              <Box mt={4}>
                <Text textAlign="center">
                  {intl.formatMessage({
                    id: 'BaseLoadView.noResults',
                    defaultMessage: 'You currently have no saved views.',
                    description:
                      'Message displayed when no saved views match the search criteria',
                  })}
                </Text>
              </Box>
            )}
          </Layout>
        </Layout>
      </ContentContainer>

      <Modal
        isVisible={confirmVisible}
        title={intl.formatMessage({
          id: 'BaseLoadView.deleteViewTitle',
          defaultMessage: 'Delete View?',
          description: 'Title for delete view confirmation modal',
        })}
        onHide={handleCloseDelete}
        onCancel={handleCloseDelete}
        primaryAction={{
          disabled: isDeleting,
          text: intl.formatMessage({
            id: 'BaseLoadView.deleteButton',
            defaultMessage: 'Delete',
            description: 'Button text for deleting a view',
          }),
          onClick: handleDelete,
          level: 'danger',
        }}
      >
        <ContentContainer loadingContent={{ isLoading: isDeleting }}>
          <Text>
            {intl.formatMessage(
              {
                id: 'BaseLoadView.deleteViewConfirmText',
                defaultMessage:
                  'Are you sure you want to delete {viewName}? You won’t be able to undo this action.',
                description:
                  'Confirmation text for deleting a view. viewName is the name of the view. Example: viewName = "My View"',
              },
              { viewName: <Text variant="body-bold">{selected?.name}</Text> },
            )}
          </Text>
        </ContentContainer>
      </Modal>

      <Modal
        isVisible={renameVisible}
        title="Rename View"
        primaryAction={{
          text: 'Rename',
          icon: 'check',
          onClick: handleRename,
          disabled: isRenaming,
        }}
        onCancel={handleCloseRename}
        onHide={handleCloseRename}
      >
        <ContentContainer loadingContent={{ isLoading: isRenaming }}>
          <TextInput
            placeholder={intl.formatMessage({
              id: 'baseLoadView.viewNamePlaceholder',
              defaultMessage: 'View Name',
              description: 'Placeholder text for view name input',
            })}
            onChange={setNewName}
            name="New name"
            value={newName}
          />
        </ContentContainer>
      </Modal>
    </Panel>
  );
};

export default BaseLoadView;
